import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from '@/components/Layout/Sidebar';
import TopBar from '@/components/Layout/TopBar';
import { TopBarSlotContext } from '@/components/Layout/TopBarSlot';
import UIContextBridge from '@/components/SAM/UIContextBridge';
import { cn } from '@/utils/cn';

const DashboardLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarHidden, setDesktopSidebarHidden] = useState(() =>
    localStorage.getItem('desktopSidebarHidden') === 'true'
  );
  // The TopBar exposes a slot element; pages (e.g. AI Chat) portal controls into it.
  const [topBarSlot, setTopBarSlot] = useState<HTMLElement | null>(null);
  const { pathname } = useLocation();
  // The AI Chat owns its full surface (dark, edge-to-edge), so it opts out of the
  // standard gray page padding that every other module page uses.
  const fullBleed = pathname === '/chat';

  const [isDesktopViewport, setIsDesktopViewport] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  );

  const handleMenuClick = () => {
    if (isDesktopViewport) {
      const next = !desktopSidebarHidden;
      setDesktopSidebarHidden(next);
      localStorage.setItem('desktopSidebarHidden', String(next));
    } else {
      setSidebarOpen(true);
    }
  };

  // Auto-close the mobile drawer when the viewport grows to lg+; track desktop breakpoint reactively.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handle = (e: MediaQueryListEvent) => {
      setIsDesktopViewport(e.matches);
      if (e.matches) setSidebarOpen(false);
    };
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  return (
    <div className="flex h-screen bg-ui font-poppins" style={{ height: '100dvh' }}>
      {/* Always-mounted situational-awareness bridge (lives here, not in ChatPage, so SAM can
          read the current page/mode/view from anywhere — incl. the future dockable chat). */}
      <UIContextBridge />

      {/* Mobile/tablet backdrop — tap outside to close */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar — off-canvas drawer on mobile/tablet, static column on lg+ */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 transition-all duration-300 ease-in-out',
          'lg:relative lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          desktopSidebarHidden ? 'lg:w-0 lg:overflow-hidden lg:opacity-0' : ''
        )}
      >
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Page content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar onMenuClick={handleMenuClick} dark={fullBleed} slotRef={setTopBarSlot} />
        <TopBarSlotContext.Provider value={topBarSlot}>
          <main className={cn('flex-1 overflow-y-auto', !fullBleed && 'p-4 md:p-6')}>
            <Outlet />
          </main>
        </TopBarSlotContext.Provider>
      </div>
    </div>
  );
};

export default DashboardLayout;
